import * as Sentry from "@sentry/react";
import { createRoot } from "react-dom/client";
import { HelmetProvider } from "react-helmet-async";
import App from "./App";
import "./index.css";
import {
  ensureCsrfToken,
  getCsrfTokenFromCookie,
  isMutationMethod,
  isSameOriginRequest,
} from "./lib/csrf";
import { restoreZoom } from "./hooks/use-zoom";

const CSRF_HEADER = "X-CSRF-Token";

const _originalFetch = window.fetch.bind(window);

async function readErrorBody(res: Response): Promise<{ error?: string } | null> {
  try {
    const cloned = res.clone();
    const data = await cloned.json();
    return data && typeof data === "object" ? data : null;
  } catch {
    return null;
  }
}

window.fetch = async function csrfFetch(
  input: RequestInfo | URL,
  init?: RequestInit,
): Promise<Response> {
  const method = (
    init?.method ??
    (input instanceof Request ? input.method : "GET")
  ).toUpperCase();
  const sameOrigin = isSameOriginRequest(input);
  const isMutation = sameOrigin && isMutationMethod(method);

  if (isMutation && !getCsrfTokenFromCookie()) {
    // Coalesced bootstrap: if multiple mutations race, they all await the
    // same in-flight GET /api/auth/csrf so the cookie is seeded once.
    await ensureCsrfToken();
  }

  const buildHeaders = () => {
    const merged = new Headers(init?.headers);
    if (isMutation) {
      const token = getCsrfTokenFromCookie();
      if (token && !merged.has(CSRF_HEADER)) {
        merged.set(CSRF_HEADER, token);
      }
    }
    return merged;
  };

  const response = await _originalFetch(input, {
    ...init,
    headers: buildHeaders(),
  });

  if (isMutation && response.status === 403) {
    const body = await readErrorBody(response);
    const errMsg = body?.error;
    if (errMsg === "CSRF token missing" || errMsg === "CSRF token invalid") {
      // Force a fresh token, then retry the original request exactly once.
      await ensureCsrfToken(true);
      return _originalFetch(input, {
        ...init,
        headers: buildHeaders(),
      });
    }
  }

  return response;
};

if (import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT || import.meta.env.MODE,
    tracesSampleRate: 0,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
  });
}

const _rdtCid = new URLSearchParams(window.location.search).get("rdt_cid");
if (_rdtCid) {
  localStorage.setItem("rdt_cid", _rdtCid);
}

restoreZoom();

createRoot(document.getElementById("root")!).render(
    <Sentry.ErrorBoundary
      fallback={
        <div className="flex min-h-screen items-center justify-center p-8 text-center">
          <div>
            <h1 className="text-2xl font-semibold mb-2">Something went wrong</h1>
            <p className="text-muted-foreground mb-4">
              An unexpected error occurred. Our team has been notified.
            </p>
            <button
              className="px-4 py-2 bg-primary text-primary-foreground rounded-md"
              onClick={() => window.location.reload()}
            >
              Reload page
            </button>
          </div>
        </div>
      }
    >
      <HelmetProvider>
        <App />
      </HelmetProvider>
    </Sentry.ErrorBoundary>
  );
